import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  private toastMessage = new BehaviorSubject<string | null>(null);
  toastMessage$ = this.toastMessage.asObservable();
  private currentTimeout: any = null;

  // Progress toast: a sticky toast for work that runs in the background while the
  // user keeps browsing. It cycles through status lines instead of auto-dismissing.
  private inProgress = new BehaviorSubject<boolean>(false);
  inProgress$ = this.inProgress.asObservable();
  private progressMessages: string[] = [];
  private progressIndex = 0;
  private progressTimer: any = null;

  showToast(message: string, duration: number = 3000) {
    // Clear any existing timeout
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
    }

    this.toastMessage.next(message);

    if (duration > 0) {
      this.currentTimeout = setTimeout(() => {
        // Don't blank out a background job's toast — fall back to its current status line
        this.toastMessage.next(this.inProgress.value ? this.progressMessages[this.progressIndex] : null);
        this.currentTimeout = null;
      }, duration);
    }
  }

  // Update message without resetting the timer
  updateToast(message: string) {
    this.toastMessage.next(message);
  }

  // Hide toast immediately
  hideToast() {
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    // A sticky progress toast outlives ordinary toasts — only hideProgressToast() ends it.
    this.toastMessage.next(this.inProgress.value ? this.progressMessages[this.progressIndex] : null);
  }

  /**
   * Show a sticky toast that rotates through `messages` until hideProgressToast() is
   * called. Nothing is blocked while it runs — the user can keep using the app.
   */
  showProgressToast(messages: string[], intervalMs: number = 3000) {
    if (!messages?.length) return;
    this.clearProgressTimer();
    if (this.currentTimeout) {
      clearTimeout(this.currentTimeout);
      this.currentTimeout = null;
    }
    this.progressMessages = messages;
    this.progressIndex = 0;
    this.inProgress.next(true);
    this.toastMessage.next(messages[0]);
    this.progressTimer = setInterval(() => {
      this.progressIndex = (this.progressIndex + 1) % this.progressMessages.length;
      this.toastMessage.next(this.progressMessages[this.progressIndex]);
    }, intervalMs);
  }

  /** End the sticky toast, optionally handing over to a normal auto-dismissing one. */
  hideProgressToast(finalMessage?: string, duration: number = 4000) {
    this.clearProgressTimer();
    this.progressMessages = [];
    this.progressIndex = 0;
    this.inProgress.next(false);
    if (finalMessage) {
      this.showToast(finalMessage, duration);
    } else {
      this.hideToast();
    }
  }

  private clearProgressTimer() {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
      this.progressTimer = null;
    }
  }
}
